import type { TripRouteShape } from './routeShape';
import type { Framing } from './framing';

/**
 * The story sticker is painted straight onto a canvas rather than screenshotted
 * from the DOM: the design leans on `backdrop-filter`, which no DOM-to-image
 * library reproduces, and the same painter then drives both the live preview and
 * the exported PNG so what the user arranges is exactly what Instagram gets.
 */

export const STICKER_WIDTH = 1080;
export const STICKER_HEIGHT = 1920;

export const INK = '#0b1420';
export const PAPER = '#faf7f0';
export const SAND = '#d9b382';

// The three faces the app already loads (index.css). Nothing here pulls in a
// webfont of its own: a sticker used once a trip can't cost every page a font.
export const SERIF = '"Fraunces", Georgia, serif';
export const SANS = '"Inter", system-ui, sans-serif';
export const MONO = '"JetBrains Mono", monospace';

export interface StickerStat {
  label: string;
  value: string;
}

export type StickerGradient = readonly [string, string];

/** `paper` is the odd one out: it's the light background, and it flips the whole
 *  sticker to ink-on-cream — see `StickerBackground.tone`. */
export const STICKER_GRADIENTS: Record<string, StickerGradient> = {
  paper: ['#faf7f0', '#efe6d6'],
  ink:   ['#111d2b', '#05090f'],
};

export const LIGHT_GRADIENTS = ['paper'];

export interface StickerBackground {
  /** An image already decoded and safe to draw (same-origin blob or a bitmap). */
  image: CanvasImageSource | null;
  imageWidth: number;
  imageHeight: number;
  /** Zoom and pan the user gave the photo by hand. */
  framing: Framing;
  gradient: StickerGradient;
  /**
   * Which way the type has to read. A photo is always treated as dark because
   * the templates scrim it; only the cream background is light, and there the
   * map drops its panel and sits straight on the page.
   */
  tone: 'dark' | 'light';
}

export interface StickerSpec {
  title: string;
  subtitle: string;
  stats: StickerStat[];
  background: StickerBackground;
  /** Whole-trip geometry; the renderer fits it to its own box. */
  routeShape: TripRouteShape | null;
  watermark: string;
}

/** One entry per face the templates draw with — family, weight and style. */
const FONT_SPECS = [
  `italic 300 92px ${SERIF}`,
  `italic 400 30px ${SERIF}`,
  `400 86px ${SERIF}`,
  `600 44px ${SERIF}`,
  `400 13px ${MONO}`,
  `500 22px ${MONO}`,
  `600 20px ${SANS}`,
];

let fontsReady: Promise<void> | null = null;

/**
 * Canvas silently falls back to a default face when a font hasn't loaded yet, so
 * every text size we draw has to be resolved before the first paint.
 */
export function ensureStickerFonts(): Promise<void> {
  if (!fontsReady) {
    fontsReady = Promise.all(FONT_SPECS.map(spec => document.fonts.load(spec)))
      .then(() => undefined)
      .catch(() => undefined);
  }
  return fontsReady;
}
